import {computed, inject, InjectionToken, Injector} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {patchState, signalStore, withComputed, withMethods, withState} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {catchError, EMPTY, filter, first, map, Observable, of, pipe, switchMap, tap} from 'rxjs';
import {HOME_URL} from 'src/app/models/home/navigation.model';
import {TenantAccount} from 'src/app/services/accounts/account.model';
import {TenantAccountService} from 'src/app/services/accounts/tenant-account.service';
import {AuthenticationService} from 'src/app/services/auth/authentication.service';
import {Permission, tenantHasPermission} from 'src/app/services/auth/permissions.service';
import {LoadError, toLoadError} from 'src/app/services/shared/load-error.model';
import {stateChanges} from 'src/app/services/shared/state-changes';
import {TenantOverview, TenantService} from 'src/app/services/tenant/tenant.service';
import {isTenantSuspended} from 'src/app/utils/tenant-plan.utils';

/** A full page load — a token so the specs can stub it (window.location can't be spied on). */
export const PAGE_LOAD = new InjectionToken<(url: string) => void>('PAGE_LOAD', {
	providedIn: 'root',
	factory: () => (url: string) => window.location.assign(url),
});

interface CurrentTenantState {
	/** The signed-in user's workspaces, null until they are loaded. */
	tenants: TenantOverview[] | null;
	/** The active workspace, undefined until one is selected. */
	currentTenant: TenantOverview | undefined;
	/** True while the workspaces load — the shell waits for it; false inside the shell. */
	initializing: boolean;
	/** The active workspace's application count (menu, applications shell), undefined until fetched, null when it failed. */
	applicationCount: number | null | undefined;
	/** The signed-in user's membership of the active workspace, undefined until fetched. */
	currentAccount: TenantAccount | undefined;
	/** Why the workspaces could not be loaded: the shell shows it instead of staying blank (KAB-2441). */
	loadError: LoadError | null;
	/** Why that membership could not be fetched, null otherwise (KAB-2441): `refreshTenantAccount()` tries again. */
	currentAccountError: LoadError | null;
}

/**
 * The active workspace (ADR-0001 §4): the user's workspaces, the one this page runs on, its application count and the
 * user's membership, as signals — and the bootstrap that selects it (shell, guards, onboarding).
 *
 * One page load = one workspace: selecting another one reloads the app on its home, so nothing loaded for the previous
 * one survives. The shell only renders once it is loaded: inside the shell, read it once (`requireTenantId()`).
 */
export const CurrentTenantStore = signalStore(
	{providedIn: 'root'},
	withState<CurrentTenantState>({tenants: null, currentTenant: undefined, initializing: true, applicationCount: undefined, currentAccount: undefined, currentAccountError: null, loadError: null}),
	withComputed(({tenants, currentTenant}) => ({
		/** The active workspace id, undefined until one is selected. */
		currentTenantId: computed(() => currentTenant()?.configuration.id),
		/** True once the workspaces are loaded and the user belongs to none. */
		hasNoTenant: computed(() => tenants()?.length === 0),
		/**
		 * Neither active nor on a running trial: the workspace keeps only its blocking page (KAB-2399).
		 * ponytail: read when the tenant loads, not ticking — a trial ending mid-session is caught on the next load.
		 */
		isSuspended: computed(() => isTenantSuspended(currentTenant())),
	})),
	withMethods((store) => {
		const tenantService = inject(TenantService);
		const tenantAccountService = inject(TenantAccountService);
		const authentication = inject(AuthenticationService);
		const activatedRoute = inject(ActivatedRoute);
		const router = inject(Router);
		const loadPage = inject(PAGE_LOAD);
		const state$ = stateChanges(store, inject(Injector));

		/** The workspace this page load runs on: another one gets a page load of its own. */
		let pageTenantId: string | undefined;
		let initializeStarted = false;
		/** The workspace the last initialize() asked for, loaded again by retry(). */
		let requestedTenantId: string | undefined;

		const userId = (): string | undefined => authentication.getUserInfo()?.id;

		const fetchApplicationCount = (tenant: TenantOverview): void => {
			// A suspended workspace gets a 403 on its data (KAB-2399).
			if (!isTenantSuspended(tenant) && tenantHasPermission(tenant, Permission.APPLICATIONS_READ)) {
				tenantService.getApplicationCount(tenant.configuration.id).subscribe({
					next: count => patchState(store, {applicationCount: count.count}),
					// Shown as a dash, never as 0 (KAB-2441).
					error: () => patchState(store, {applicationCount: null}),
				});
			}
		};

		const fetchCurrentAccount = (tenantId: string): void => {
			patchState(store, {currentAccountError: null});
			tenantAccountService.getSelfTenantAccount(tenantId).subscribe({
				next: account => patchState(store, {currentAccount: account ?? undefined}),
				error: error => patchState(store, {currentAccountError: toLoadError(error)}),
			});
		};

		/** The cost displays (CostPipe, exports…) read the currency symbol from localStorage. */
		const setCurrent = (tenant: TenantOverview): void => {
			localStorage.setItem('currency', tenant.configuration.currency.symbol);
			patchState(store, {currentTenant: tenant});
		};

		/** The URL's workspace, else the one this user last opened, else their default one, else the first. */
		const defaultTenantId = (tenants: TenantOverview[]): string | undefined => {
			const known = (id: string | null | undefined): id is string => !!id && tenants.some(t => t.configuration.id === id);
			return [activatedRoute.snapshot.queryParamMap.get('tenant'), rememberedTenant(userId())].find(known)
				?? tenants.find(t => t.configuration.isDefault)?.configuration.id
				?? tenants[0]?.configuration.id;
		};

		/** Selects a loaded workspace — or reloads the app on its home when this page already runs another one. */
		const select = (tenantId: string | undefined, skipNavigation = false): void => {
			const tenant = store.tenants()?.find(t => t.configuration.id === tenantId);
			if (!tenant) {
				patchState(store, {currentTenant: undefined});
				if (!skipNavigation) {
					router.navigate([HOME_URL]);
				}
				return;
			}
			rememberTenant(userId(), tenant.configuration.id);
			if (pageTenantId && pageTenantId !== tenant.configuration.id) {
				// Like the post-login landing: the reloaded app opens behind the splash, held for 2 loops of the logo (index.html).
				sessionStorage.setItem('show-splash', '1');
				loadPage(`${HOME_URL}?tenant=${tenant.configuration.id}`);
				return;
			}
			pageTenantId = tenant.configuration.id;
			setCurrent(tenant);
			fetchCurrentAccount(tenant.configuration.id);
			fetchApplicationCount(tenant);
			// Not from a guard (ensureInitialized): the router is resolving the requested route, and navigate([], …) would
			// read as a navigation to '/', cancelling it and bouncing the user home.
			if (!skipNavigation) {
				router.navigate([], {queryParams: {tenant: tenant.configuration.id}, queryParamsHandling: 'merge'});
			}
		};

		const loadTenants = rxMethod<{tenantId?: string; skipNavigation: boolean}>(pipe(
			// A newer initialize() cancels a /tenants call still in flight, which would otherwise select its (older) tenant.
			switchMap(({tenantId, skipNavigation}) => tenantService.getTenants().pipe(
				tap(tenants => {
					patchState(store, {tenants});
					select(tenantId || defaultTenantId(tenants), skipNavigation);
				}),
				// Not a finalize(): a load cancelled by a newer initialize() must not flag the store ready.
				tap({complete: () => patchState(store, {initializing: false})}),
				catchError((error: unknown) => {
					patchState(store, {initializing: false, loadError: toLoadError(error)});
					return EMPTY;
				}),
			)),
		));

		const initialize = (tenantId?: string, skipNavigation = false): void => {
			initializeStarted = true;
			requestedTenantId = tenantId;
			patchState(store, {initializing: true, loadError: null});
			loadTenants({tenantId, skipNavigation});
		};

		/** Emits the active workspace once it is loaded, then completes — synchronously when it already is. */
		const whenLoaded = (): Observable<TenantOverview> => state$.pipe(
			map(state => state.currentTenant),
			filter((tenant): tenant is TenantOverview => !!tenant),
			first(),
		);

		return {
			/**
			 * The active workspace id, for code running inside the shell (it only renders once a workspace is loaded, and a
			 * page load never switches it). Throws when called before — the login, the onboarding before its workspace
			 * exists — rather than calling the API with `undefined`.
			 */
			requireTenantId(): string {
				const tenantId = store.currentTenantId();
				if (!tenantId) {
					throw new Error('CurrentTenantStore.requireTenantId(): no workspace loaded yet — read it inside the shell or wait for currentTenantId()');
				}
				return tenantId;
			},
			/** For root services, built before the shell renders. Components inside the shell read the signals instead. */
			whenLoaded,
			/** Loads the user's workspaces and selects `tenantId` (the onboarding), else the default one (the shell). */
			initialize(tenantId?: string): void {
				initialize(tenantId);
			},
			/**
			 * For route guards: resolves once a workspace is loaded, starting the load on a hard refresh. Fails when the
			 * load fails: a guard left pending would hold the first navigation, hence the whole app, on a blank page.
			 */
			ensureInitialized(): Observable<TenantOverview> {
				const current = store.currentTenant();
				if (current) {
					return of(current);
				}
				if (!initializeStarted) {
					// skipNavigation: a guard runs mid-navigation, a router.navigate() from the load would cancel it.
					initialize(undefined, true);
				}
				return state$.pipe(
					filter(state => !!state.currentTenant || !!state.loadError),
					first(),
					map(state => {
						if (!state.currentTenant) {
							throw new Error('The workspaces could not be loaded');
						}
						return state.currentTenant;
					}),
				);
			},
			/** Loads the workspaces again after a failure ("Réessayer", or the connection back). */
			retry(): void {
				initialize(requestedTenantId);
			},
			/** Safe to call inside a computed() — reads the currentTenant signal. */
			hasPermission(permission: Permission): boolean {
				const tenant = store.currentTenant();
				return !!tenant && tenantHasPermission(tenant, permission);
			},
			hasAnyPermission(permissions: Permission[]): boolean {
				const tenant = store.currentTenant();
				return !!tenant && permissions.some(permission => tenantHasPermission(tenant, permission));
			},
			/** Another workspace reloads the app on its home. */
			changeTenant(tenantId: string): void {
				select(tenantId);
			},
			/** Re-reads the active workspace after a rename, logo or currency change, so its readers follow. */
			refreshCurrentTenant(): void {
				const currentId = store.currentTenantId();
				tenantService.getTenants().subscribe(tenants => {
					patchState(store, {tenants});
					const tenant = tenants.find(t => t.configuration.id === currentId);
					if (tenant) {
						setCurrent(tenant);
					}
				});
			},
			/** Re-fetches the application count after an application is added, archived or restored. */
			refreshApplicationCount(): void {
				const tenant = store.currentTenant();
				if (tenant) {
					fetchApplicationCount(tenant);
				}
			},
			/** Re-fetches the signed-in user's membership after their profile changed. */
			refreshTenantAccount(): void {
				const tenantId = store.currentTenantId();
				if (tenantId) {
					fetchCurrentAccount(tenantId);
				}
			},
		};
	}),
);

/** Same-name type alias so code can still annotate `: CurrentTenantStore`. */
export type CurrentTenantStore = InstanceType<typeof CurrentTenantStore>;

/** The last workspace each user of this browser opened: `tenantId` holds [{userId, tenantId}]. */
interface StoredUserAndTenantID {
	userId: string;
	tenantId: string;
}

function storedTenants(): StoredUserAndTenantID[] {
	try {
		const stored: unknown = JSON.parse(localStorage.getItem('tenantId') ?? '[]');
		return Array.isArray(stored) ? stored : [];
	} catch {
		// Before May 2022 the key held a bare workspace id, not JSON: start afresh rather than block the bootstrap.
		return [];
	}
}

function rememberedTenant(userId: string | undefined): string | undefined {
	return storedTenants().find(entry => entry.userId === userId)?.tenantId;
}

function rememberTenant(userId: string | undefined, tenantId: string): void {
	if (userId) {
		localStorage.setItem('tenantId', JSON.stringify([...storedTenants().filter(entry => entry.userId !== userId), {userId, tenantId}]));
	}
}
